let ytApiLoaded = false;
let ytApiPromise = null;

function loadYTApi() {
  if (ytApiLoaded) return Promise.resolve();
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      ytApiLoaded = true;
      resolve();
      return;
    }
    window.onYouTubeIframeAPIReady = () => {
      ytApiLoaded = true;
      resolve();
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return ytApiPromise;
}

const IS_MOBILE = window.innerWidth < 750;

class PPVideoGallery extends HTMLElement {
  #controller;
  #activeVideoIndex = 0;
  #ytPlayers = new Map();
  #isNavigating = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.querySelectorAll('[data-vg-play]').forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          const wrap = btn.closest('.vg__yt-wrap');
          if (wrap) {
            if (IS_MOBILE) {
              this.#loadYouTubeMobile(wrap);
            } else {
              this.#loadYouTubeDesktop(wrap);
            }
          }
        },
        { signal }
      );
    });

    // Desktop: simple thumb click
    if (!IS_MOBILE) {
      this.querySelectorAll('.vg__thumb').forEach((thumb) => {
        thumb.addEventListener(
          'click',
          () => {
            const idx = parseInt(thumb.dataset.thumbIndex, 10);
            if (idx !== this.#activeVideoIndex) this.#switchVideo(idx);
          },
          { signal }
        );
      });
    }

    this.querySelectorAll('.vg__lang-select').forEach((select) => {
      select.addEventListener(
        'change',
        () => {
          const videoIdx = parseInt(select.dataset.videoIndex, 10);
          this.#switchLanguage(videoIdx, select.value);
        },
        { signal }
      );
    });

    // Mobile: carousel scroll sync + dots
    if (IS_MOBILE) {
      this.#initMobileCarousel(signal);
    }

    document.addEventListener(
      'media:started-playing',
      (e) => {
        if (!this.contains(e.target)) this.#pauseAllVideos();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#ytPlayers.forEach((p) => {
      try { p.destroy(); } catch {}
    });
    this.#ytPlayers.clear();
  }

  // Mobile: create iframe directly (synchronous, preserves user gesture for autoplay)
  #loadYouTubeMobile(wrap) {
    if (wrap.dataset.loaded) return;
    wrap.dataset.loaded = 'true';

    const videoId = wrap.dataset.videoId;
    if (!videoId) return;

    const poster = wrap.querySelector('[data-vg-play]');
    if (poster) poster.style.display = 'none';

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&iv_load_policy=3&playsinline=1&enablejsapi=1`;
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.setAttribute('data-video-type', 'youtube');
    iframe.style.cssText = 'width:100%;height:100%;border:0;display:block;';
    wrap.appendChild(iframe);
  }

  // Desktop: use YT.Player for state change events (dropdown show/hide)
  async #loadYouTubeDesktop(wrap) {
    if (wrap.dataset.loaded) return;
    wrap.dataset.loaded = 'true';

    const videoId = wrap.dataset.videoId;
    if (!videoId) return;

    const poster = wrap.querySelector('[data-vg-play]');
    if (poster) poster.style.display = 'none';

    const playerEl = wrap.closest('.vg__player');
    const dropdown = playerEl?.querySelector('.vg__lang-dropdown');
    if (dropdown) dropdown.classList.add('vg__lang-dropdown--hidden');

    await loadYTApi();

    const holder = document.createElement('div');
    holder.id = `vg-yt-${videoId}-${Date.now()}`;
    holder.style.cssText = 'width:100%;height:100%;';
    wrap.appendChild(holder);

    const gallery = this;
    const player = new YT.Player(holder.id, {
      videoId: videoId,
      host: 'https://www.youtube-nocookie.com',
      playerVars: {
        autoplay: 1,
        rel: 0,
        modestbranding: 1,
        iv_load_policy: 3,
        playsinline: 1,
      },
      events: {
        onStateChange(event) {
          gallery.#onYTStateChange(playerEl, event.data);
        },
      },
    });

    this.#ytPlayers.set(videoId, player);
  }

  #onYTStateChange(playerEl, state) {
    if (!playerEl) return;
    const dropdown = playerEl.querySelector('.vg__lang-dropdown');
    if (!dropdown) return;

    if (state === 1 || state === 3) {
      dropdown.classList.add('vg__lang-dropdown--hidden');
    } else {
      dropdown.classList.remove('vg__lang-dropdown--hidden');
    }
  }

  #switchVideo(newIndex, fromScroll = false) {
    const oldIndex = this.#activeVideoIndex;
    this.#pauseVideoAt(oldIndex);

    this.querySelectorAll('.vg__player').forEach((p, i) => {
      p.classList.toggle('vg__player--active', i === newIndex);
    });

    this.querySelectorAll('.vg__info-item').forEach((item, i) => {
      item.classList.toggle('vg__info-item--active', i === newIndex);
    });

    this.querySelectorAll('.vg__thumb').forEach((t, i) => {
      t.classList.toggle('vg__thumb--active', i === newIndex);
    });

    this.#updateDots(newIndex);

    this.#activeVideoIndex = newIndex;

    // Scroll thumb into view (skip if triggered by scroll itself)
    if (!fromScroll && IS_MOBILE) {
      const track = this.querySelector('.vg__thumbs');
      if (track) this.#snapThumbTo(track, newIndex);
    }
  }

  /* === MOBILE CAROUSEL — JS-based snap (no CSS scroll-snap) === */

  #initMobileCarousel(signal) {
    const track = this.querySelector('.vg__thumbs');
    if (!track) return;

    // Ensure first thumb is visible on load
    track.scrollLeft = 0;

    // Touch tracking: limit swipe to ±1 item
    let touchStartX = 0;
    let touchStartIndex = 0;

    track.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartIndex = this.#activeVideoIndex;
    }, { signal, passive: true });

    track.addEventListener('touchend', (e) => {
      const dx = (e.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
      const thumbCount = track.querySelectorAll('.vg__thumb').length;
      const threshold = 30; // min px to count as swipe

      let target = touchStartIndex;
      if (dx < -threshold && touchStartIndex < thumbCount - 1) {
        target = touchStartIndex + 1; // swipe left → next
      } else if (dx > threshold && touchStartIndex > 0) {
        target = touchStartIndex - 1; // swipe right → prev
      }

      // Kill momentum before snapping — toggle overflow to stop inertia
      track.style.overflow = 'hidden';
      void track.offsetHeight;
      track.style.overflow = '';

      if (target !== this.#activeVideoIndex) {
        this.#switchVideo(target, true);
      }
      this.#snapThumbTo(track, target);
    }, { signal, passive: true });

    // Dot clicks
    this.querySelectorAll('.vg__dot').forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.dotIndex, 10);
        if (idx !== this.#activeVideoIndex) this.#switchVideo(idx);
      }, { signal });
    });

    // Thumb click → navigate + switch
    track.querySelectorAll('.vg__thumb').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.dataset.thumbIndex, 10);
        if (idx !== this.#activeVideoIndex) {
          this.#switchVideo(idx, true);
          this.#snapThumbTo(track, idx);
        }
      }, { signal });
    });
  }

  #findClosestThumb(track) {
    const thumbs = track.querySelectorAll('.vg__thumb');
    const maxScroll = track.scrollWidth - track.offsetWidth;
    const current = track.scrollLeft;
    let closest = 0;
    let minDist = Infinity;
    thumbs.forEach((t, i) => {
      // Compare clamped target positions so edge items are reachable
      const target = Math.max(0, Math.min(maxScroll,
        t.offsetLeft + t.offsetWidth / 2 - track.offsetWidth / 2));
      const dist = Math.abs(target - current);
      if (dist < minDist) { minDist = dist; closest = i; }
    });
    return closest;
  }

  #snapThumbTo(track, idx) {
    const thumb = track.querySelectorAll('.vg__thumb')[idx];
    if (!thumb) return;

    // Clamp scroll so edge items work (scrollTo clips to 0..maxScroll)
    const targetLeft = thumb.offsetLeft + thumb.offsetWidth / 2 - track.offsetWidth / 2;

    this.#isNavigating = true;
    track.scrollTo({ left: targetLeft, behavior: 'smooth' });

    let endTimer;
    const onEnd = () => {
      clearTimeout(endTimer);
      endTimer = setTimeout(() => {
        this.#isNavigating = false;
        track.removeEventListener('scroll', onEnd);
      }, 100);
    };
    track.addEventListener('scroll', onEnd, { passive: true });
    // Fallback if already at position (no scroll event fires)
    setTimeout(() => { this.#isNavigating = false; }, 400);
  }

  #updateDots(idx) {
    this.querySelectorAll('.vg__dot').forEach((dot) => {
      dot.setAttribute(
        'aria-selected',
        parseInt(dot.dataset.dotIndex, 10) === idx ? 'true' : 'false'
      );
    });
  }

  #switchLanguage(videoIndex, langValue) {
    const player = this.querySelectorAll('.vg__player')[videoIndex];
    if (!player) return;

    player.querySelectorAll('.vg__video-wrapper').forEach((wrapper) => {
      this.#pauseWrapper(wrapper);
      wrapper.classList.remove('vg__video-wrapper--active');
    });

    const target = player.querySelector(
      `.vg__video-wrapper[data-lang="${langValue}"]`
    );
    if (target) {
      target.classList.add('vg__video-wrapper--active');

      const ytWrap = target.querySelector('.vg__yt-wrap');
      if (ytWrap) {
        if (IS_MOBILE) {
          this.#loadYouTubeMobile(ytWrap);
        } else {
          this.#loadYouTubeDesktop(ytWrap);
        }
      }

      const dm = target.querySelector('deferred-media');
      if (dm && typeof dm.loadContent === 'function') {
        dm.loadContent(false);
      }
    }
  }

  #pauseVideoAt(index) {
    const player = this.querySelectorAll('.vg__player')[index];
    if (!player) return;
    const activeWrapper = player.querySelector('.vg__video-wrapper--active');
    if (activeWrapper) this.#pauseWrapper(activeWrapper);
  }

  #pauseWrapper(wrapper) {
    // Pause YT.Player instances (desktop)
    wrapper.querySelectorAll('.vg__yt-wrap[data-loaded]').forEach((w) => {
      const id = w.dataset.videoId;
      const ytPlayer = this.#ytPlayers.get(id);
      if (ytPlayer && typeof ytPlayer.pauseVideo === 'function') {
        ytPlayer.pauseVideo();
      }
    });

    // Pause iframes via postMessage (mobile + vimeo)
    wrapper.querySelectorAll('iframe[data-video-type="youtube"]').forEach((iframe) => {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          '*'
        );
      }
    });

    wrapper.querySelectorAll('iframe[data-video-type="vimeo"]').forEach((iframe) => {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage('{"method":"pause"}', '*');
      }
    });

    wrapper.querySelectorAll('video').forEach((v) => v.pause());
  }

  #pauseAllVideos() {
    this.querySelectorAll('.vg__video-wrapper').forEach((wrapper) => {
      this.#pauseWrapper(wrapper);
    });
  }
}

if (!customElements.get('pp-video-gallery')) {
  customElements.define('pp-video-gallery', PPVideoGallery);
}
